import type { SystemTableColumn } from './tableColumn';
import type { SortConfig } from './tableSorting';

interface Props<K extends string, SortKey extends K = K, T = unknown> {
    columns: readonly SystemTableColumn<K, SortKey, T>[];
    sortConfig: SortConfig<SortKey> | null;
    onSort?: (colKey: SortKey) => void;
    headerRowClassName?: string;
    headerCellClassName?: string;
    testId?: string;
}

const SystemTableHeader = <K extends string, SortKey extends K = K, T = unknown>({
    columns,
    sortConfig,
    onSort,
    headerRowClassName,
    headerCellClassName,
    testId = 'system-table-header',
}: Props<K, SortKey, T>) => (
    <thead>
        <tr className={headerRowClassName}>
            {columns.map(column => {
                const isSortable = column.sortable;
                const isClickable = isSortable && !!onSort;
                const isSorted = isSortable && sortConfig?.key === column.key;

                const className = [headerCellClassName, isClickable ? 'cursor-pointer' : undefined]
                    .filter(Boolean)
                    .join(' ');

                const handleClick = () => {
                    if (!isSortable || !onSort) return;

                    onSort(column.key);
                };

                return (
                    <th
                        key={column.key}
                        scope='col'
                        data-testid={testId}
                        className={className || undefined}
                        onClick={handleClick}
                        aria-sort={isSorted ? (sortConfig.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                    >
                        {column.label}
                        {isSorted && (
                            <span
                                aria-hidden='true'
                                className={`rioglyph rioglyph-triangle-${
                                    sortConfig.dir === 'asc' ? 'top' : 'bottom'
                                } margin-left-5`}
                            />
                        )}
                    </th>
                );
            })}
        </tr>
    </thead>
);

export default SystemTableHeader;
